import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { TwinIconProp } from '../../baseComponents/TwinIcon'
import { addOrRemoveClassList } from '../../utils/globals/components'
import { TwinDictionary, dictionaryComplexFromJsonArr, filterDictionaryNotInItems } from '../../utils/globals/dictionary'
import { ChangeValueFunc, Modify } from '../../utils/globals/types'
import useOnClickOutside from '../../utils/hooks/useOnClickOut'
import './customselect.sass'
import { CustomSelectDropdownIcon, DropDownDecider, DropDownWithSearchBar } from './DropDown'
import { CustomSelectDropDownBasePropsMod, CustomSelectValue, DropDownDeciderProps } from './DropDown/types'
import { buildCustomSelectClass } from './functions'
import { ConfigurableSelectClassProps, CustomSelectHeaderBaseProps, CustomSelectSortFunctionTy, DisplayTextFuncTy } from './types'

export type CustomSelectBaseProps = CustomSelectLogicProps & CustomSelectHeaderBaseProps & {
    className?: string
    DropDownComponent: React.FC<CustomSelectDropDownBasePropsMod>
    CustomSelectHeader?: React.FC<CustomSelectHeaderDefaultProps>
    extraDropDownParams?: TwinDictionary
    extraHeaderParams?: TwinDictionary
    sortItems?: boolean
    maxItems?: number
    sortFunction?: CustomSelectSortFunctionTy
    placeholder?: string
    hideLabel?: boolean
    skipBorderStyle?: boolean
}

export const CustomSelectBase: React.FC<CustomSelectBaseProps> = ({ className, DropDownComponent, label, name, required, items, extraDropDownParams, readOnly, maxItems, fieldName, icon, CustomSelectHeader, extraHeaderParams, sortItems, sortFunction, onClickIcon, placeholder, hideLabel, skipBorderStyle, ...logicProps }) => {
    const { ref, opened, changeOpened, changeValue, myValue, classBuilded, displayText } = useCustomSelectLogic({ ...logicProps, readOnly, items })
    const Component = CustomSelectHeaderDecider(CustomSelectHeader)
    return (
        <div className={'custom_select custom_select_white_input_parent flex flex-col gap-y-2 ' + (className || '') + ' ' + classBuilded} ref={ref} onClick={changeOpened}>
            {(label !== '' && label) && !hideLabel ?
                <label title={label} className={'cursor-pointer twin_elipsis'}>{label}{required ? <span className='ml-5 text-green-21'>*</span> : ''}</label>
                : null
            }
            <div className={'flex items-center custom_select_border ' + (skipBorderStyle ? '' : 'input_white_border_class')}>
                <Component {...{ label, name, required, opened, onFocus: changeOpened, displayText, value: myValue, readOnly, icon, onClickIcon, extraHeaderParams, items, fieldName, placeholder }} />
                { opened && <DropDownComponent items={items} onChange={changeValue} value={myValue} extraParams={extraDropDownParams} maxItems={maxItems} fieldName={fieldName} sortItems={sortItems} sortFunction={sortFunction} />}
            </div>
        </div>
    )
}

export const CustomSelectHeaderDecider = (CustomSelectHeader?: React.FC<CustomSelectHeaderDefaultProps>) => {
    if (CustomSelectHeader) {
        return CustomSelectHeader
    }
    return CustomSelectHeaderDefault
}

export interface CustomSelectHeaderDefaultProps extends CustomSelectHeaderBaseProps {
    onFocus?: () => void
    value?: string | number
    extraHeaderParams?: TwinDictionary
    displayText: string
    opened: boolean
    readOnly?: boolean
    placeholder?: string
}

export const CustomSelectHeaderDefault: React.FC<CustomSelectHeaderDefaultProps> = ({ opened, displayText, label, required, icon, extraHeaderParams, onClickIcon, placeholder, ...input }) => {
    return (
        <div className={'custom_select_header custom_select_header_white_input items-center cursor-pointer '}>
            <input type='text' className='input_hidden' {...input} required={required} onChange={() => undefined} />
            <div className='custom_select_header_icon'>
                <div className='custom_select_header_text'>
                    {(input.value === undefined || input.value === null || input.value === "") || !displayText ? <label title={placeholder ?? label} className={'cursor-pointer label_placeholder twin_elipsis'}>{placeholder ?? label}</label> : null}
                    <div className='custom_select_item_text' title={displayText}>{displayText}</div>
                </div>
                <CustomSelectDropdownIcon opened={opened} readOnly={input.readOnly} icon={icon} onClickIcon={onClickIcon} />
            </div>
        </div>
    )
}

interface CustomSelectHeaderProps extends CustomSelectHeaderBaseProps {
    onFocus?: () => void
    value?: string | number
    displayText: string
    opened: boolean
    readOnly?: boolean
    placeholder?: string
}

export const CustomSelectHeader: React.FC<CustomSelectHeaderProps> = ({ opened, displayText, label, required, icon, onClickIcon, placeholder, ...input }) => {
    return (
        <div className={'custom_select_header cursor-pointer custom_select_header_white_input items-center '}> <input type='text' className='input_hidden' {...input} required={required} onChange={() => undefined} />
            <div className='custom_select_header_icon'>
                <div className='custom_select_header_text'>
                    {(input.value === undefined || input.value === null || input.value === "") || !displayText ?<label title={placeholder ?? label} className={'cursor-pointer label_placeholder twin_elipsis'}>{placeholder ?? label}</label> : null}
                    <div className='custom_select_item_text' title={displayText}>{displayText}</div>
                </div>
                <CustomSelectDropdownIcon opened={opened} readOnly={input.readOnly} icon={icon} onClickIcon={onClickIcon} />
            </div>
        </div>
    )
}

type CustomSelectLogicProps = {
    defaultValue?: never
    onChange?: (value: any) => void
    value?: CustomSelectValue
    configurableClassProps?: ConfigurableSelectClassProps
    readOnly?: boolean
    displayTextFunc: DisplayTextFuncTy
    items: TwinDictionary | TwinDictionary[]
    fieldName?: string
    isStateLess?: boolean
}

const useCustomSelectLogic = ({ onChange, configurableClassProps, readOnly, value, displayTextFunc, items, fieldName, isStateLess }: CustomSelectLogicProps) => {
    const [opened, setOpened] = useState<boolean | null>(null)
    const [myValue, setMyValue] = useState(value ?? '')
    const ref = useRef<any>(null)

    useOnClickOutside(ref, () => setOpened(false))

    const displayText = displayTextFunc(items, myValue, fieldName)

    const changeOpened = useCallback(() => {
        if (!readOnly) {
            setOpened((oldOpened) => !oldOpened)
        }
    }, [setOpened, readOnly])

    const changeValue: ChangeValueFunc = useCallback((myvalue) => {
        if (!isStateLess){
            setMyValue(myvalue)
        }
        onChange?.(myvalue)
    }, [setMyValue, onChange, isStateLess])

    useEffect(() => {
        setMyValue(value ?? '')
    }, [value])

    useEffect(() => {
        if (opened === false) {
            if (ref && ref.current) {
                const isValid = ref?.current?.querySelector('input')?.checkValidity()
                addOrRemoveClassList(ref.current, 'custom_select_error', !isValid)
            }
        }else{
            if (ref && ref.current) {
                const inputParentRef = ref?.current?.querySelector('.input_with_icon')
                if(inputParentRef){
                    inputParentRef.children[0].focus()
                }
            }
        }
    }, [opened, ref])

    const myopened = !!opened
    const classBuilded = buildCustomSelectClass({
        opened: myopened,
        labelUp: myValue !== '' && displayText !== undefined && displayText !== '',
        ...configurableClassProps
    })
    return { opened: myopened, ref, myValue, changeOpened, changeValue, classBuilded, displayText }
}

export type CustomSelectProps = Modify<CustomSelectBaseProps, DropDownDeciderProps & {
    displayTextFunc?: never
    items: TwinDictionary
    maxItems?: number
    icon?: TwinIconProp
    hideLabel?: boolean
    skipBorderStyle?: boolean
}>

const CustomSelect: React.FC<CustomSelectProps> = ({ DropDownComponent, sortItems, fieldName = 'name',sortFunction, ...rest }) => {
    const Component = DropDownDecider({ DropDownComponent, sortItems, sortFunction })
    const displayTextFunc: DisplayTextFuncTy = useCallback((items, value) => {
        if (!Array.isArray(items)) {
            return items[value]?.[fieldName]
        }
        return ''
    }, [fieldName])
    return (
        <CustomSelectBase DropDownComponent={Component} {...{ fieldName, sortItems, sortFunction }} {...rest} displayTextFunc={displayTextFunc} />
    )
}

interface CustomSelectWithSearchBarProps extends CustomSelectProps { }

export const CustomSelectWithSearchBar: React.FC<CustomSelectWithSearchBarProps> = ({ items, ...all }) => {
    return (<CustomSelect items={items} {...all} DropDownComponent={DropDownWithSearchBar} />)
}

export interface CustomSelectWithNotInFilteredItemsProps extends CustomSelectProps {
    excludeItems: string[]
}

export const CustomSelectWithNotInFilteredItems: React.FC<CustomSelectWithNotInFilteredItemsProps> = ({ items, excludeItems, ...all }) => {
    const filteredItems: TwinDictionary = useMemo(() => filterDictionaryNotInItems(items, excludeItems), [items, excludeItems])
    return <CustomSelect {...all} items={filteredItems}></CustomSelect>
}
export interface CustomSelectSearchBarWithNotInFilteredItemsProps extends CustomSelectProps {
    excludeItems: string[]
}

export const CustomSelectSearchBarWithNotInFilteredItems: React.FC<CustomSelectSearchBarWithNotInFilteredItemsProps> = ({ items, excludeItems, ...all }) => {
    const filteredItems: TwinDictionary = useMemo(() => filterDictionaryNotInItems(items, excludeItems), [items, excludeItems])
    return <CustomSelectWithSearchBar {...all} items={filteredItems} />
}

export interface CustomSelectWithSearchBarNotInFilteredItemsProps extends CustomSelectWithNotInFilteredItemsProps { }

export const CustomSelectWithSearchBarNotInFilteredItems: React.FC<CustomSelectWithSearchBarNotInFilteredItemsProps> = ({ items, excludeItems, ...all }) => {
    const filteredItems: TwinDictionary = useMemo(() => filterDictionaryNotInItems(items, excludeItems), [items, excludeItems])
    return (<CustomSelect items={filteredItems} {...all} DropDownComponent={DropDownWithSearchBar} />)
}

export type CustomSelectArrComplexProps = Modify<CustomSelectBaseProps, DropDownDeciderProps & {
    items: TwinDictionary[]
    displayTextFunc?: never
    maxItems?: number
    fieldValue?: string
}>

export const CustomSelectArrComplex: React.FC<CustomSelectArrComplexProps> = ({ DropDownComponent, sortItems, items, fieldValue = 'id', fieldName = 'name', ...rest }) => {
    const Component = DropDownDecider({ DropDownComponent, sortItems })
    const dictItems = dictionaryComplexFromJsonArr(items || [], fieldValue)
    const displayTextFunc: DisplayTextFuncTy = useCallback((items, value) => {
        if (!Array.isArray(items)) {
            return items[value]?.[fieldName]
        }
        return ''
    }, [fieldName])
    return (
        <CustomSelectBase DropDownComponent={Component} items={dictItems} {...{ displayTextFunc, fieldName }} {...rest} />
    )
}


export default CustomSelect